import { useCallback, useEffect, useReducer, useRef, useState } from 'react'

import type { Course } from '../core/course.js'
import {
  currentStep,
  currentVideoUrl,
  initialPlayerState,
  isPaused,
  playerReducer,
  timerLevel,
} from '../core/player.js'
import { clearProgress, loadProgress, saveProgress } from '../core/progress.js'
import { buildFinishUrl, fetchCourse } from '../legacy/api.js'
import { VideoPlayer } from '../media/VideoPlayer.js'
import { PauseMenu } from './PauseMenu.js'
import { QuestionScreen } from './QuestionScreen.js'
import {
  ErrorScreen,
  FinishedScreen,
  LoadingScreen,
  ResultsPendingScreen,
  type Language,
} from './Screens.js'
import { useNow } from './useNow.js'

/**
 * Kontejner průchodu kurzem — propojuje stavový automat s obrazovkami.
 *
 * Sám nerozhoduje o pravidlech; veškerá logika je v `core/`, veškerá
 * znalost formátů safetutor.cz v `legacy/`. Tahle komponenta jen překládá
 * fáze na obrazovky a uživatelské akce na akce reduceru.
 */

/** Jak dlouho nechat vidět vyhodnocení odpovědi, než se pokračuje. */
const FEEDBACK_MS = 1_600

/**
 * Jak dlouho ukázat mezikrok „Modul dokončen — vyhodnocuji výsledky…“
 * po poslední otázce, než se odkryje verdikt. Čistě prezentační pauza;
 * bodování ani dokončovací URL se tím nemění.
 */
const RESULTS_PENDING_MS = 2_200

/**
 * Jak dlouho nechat vidět verdikt, než se SAMO přejde dál.
 *
 * Školení zapíše až server na dokončovací adrese — dokud se na ni nepřejde,
 * není zapsané nic. Dřív se čekalo na kliknutí na DOKONČIT a kdo zavřel
 * záložku na výsledkové obrazovce, o záznam přišel. Stará aplikace přecházela
 * hned po posledním objektu. Tlačítko na verdiktu přejde okamžitě.
 *
 * Omezený pohyb (prefers-reduced-motion) na tom nic nemění — jde jen o čas.
 */
const AUTO_FINISH_MS = 3_000

export interface CoursePlayerProps {
  /** Hodnota `lecture` — např. "modul91" nebo slug demo kurzu. */
  lecture: string
  userId: string
  language: Language
  /**
   * Zavolá se PRÁVĚ JEDNOU s cílem po dokončení — dokončovací URL, nebo
   * `successUrl`, když je vyplněná. Navigaci provádí volající a musí to být
   * navigace celého okna (`window.location.href`), ne fetch: server na
   * dokončovací adrese nastaví přihlášení a přesměruje do profilu s certifikátem.
   */
  onFinish: (url: string) => void
  /**
   * Cíl po dokončení DEMA. Když je vyplněný, dokončovací endpoint se nepoužije
   * vůbec — demo nemá uživatele a server by vrátil jen `{"stav":"ERR"}`.
   */
  successUrl?: string | undefined
  /**
   * Přejít dál samo `AUTO_FINISH_MS` po zobrazení verdiktu. Výchozí `true`;
   * vypíná to jen vývojářská pomůcka `/test-hotspoty`.
   */
  autoFinish?: boolean | undefined
  /** Uživatel ukončil kurz z nabídky. */
  onQuit: () => void
  /**
   * Přednačtený kurz — když je vyplněný, `fetchCourse` se vůbec nezavolá.
   *
   * Jen pro vývojářské pomůcky (viz `src/dev/`), které nemají co dělat
   * s reálným API. Ostrý běh tenhle prop nikdy nepředává.
   */
  course?: Course
}

export function CoursePlayer({
  lecture,
  userId,
  language,
  onFinish,
  onQuit,
  successUrl,
  autoFinish = true,
  course: preloadedCourse,
}: CoursePlayerProps): React.ReactElement {
  const [state, dispatch] = useReducer(playerReducer, undefined, initialPlayerState)
  const [attempt, setAttempt] = useState(0)
  // Verdikt se odkryje až po krátkém mezikroku „vyhodnocuji výsledky…“.
  const [resultsRevealed, setResultsRevealed] = useState(false)

  const phase = state.phase
  const step = currentStep(state)

  /* --- načtení kurzu ---------------------------------------------------- */
  useEffect(() => {
    if (preloadedCourse) {
      dispatch({ type: 'courseLoaded', course: preloadedCourse, now: Date.now() })
      return undefined
    }

    const controller = new AbortController()
    let cancelled = false

    fetchCourse(lecture, controller.signal)
      .then((course) => {
        if (cancelled) return
        // Rozdělaný kurz se obnoví, pokud sedí na načtená data.
        // Reducer si sám ověří, že uložené pozice v kurzu existují.
        const saved = loadProgress(lecture, userId, Date.now())
        dispatch({
          type: 'courseLoaded',
          course,
          now: Date.now(),
          restore: saved ?? undefined,
        })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const message = error instanceof Error ? error.message : 'Neznámá chyba'
        dispatch({ type: 'courseFailed', message })
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [lecture, userId, attempt, preloadedCourse])

  /*
   * Pojistka „přejít právě jednou“. Druhá navigace na dokončovací adresu =
   * druhý zápis a druhý e-mail správci. Ref přežije dvojí spuštění efektů
   * v React StrictMode a hlídá souběh kliknutí s automatickým přechodem.
   */
  const navigated = useRef(false)

  /* --- průběžné ukládání postupu ---------------------------------------- */
  useEffect(() => {
    // Načítání ani chyba nemají co ukládat.
    if (!state.course) return
    if (phase.kind === 'loading' || phase.kind === 'error') return
    // Po přechodu už nic — záznam se právě smazal (viz finishNow).
    if (navigated.current) return

    // Ukládá se i DOKONČENÝ kurz (kurzor za posledním krokem). Dokud se
    // nepřejde na dokončovací adresu, je výsledek jen tady v prohlížeči —
    // kdo zavře záložku na výsledkové obrazovce, po znovuotevření skončí
    // rovnou na verdiktu a výsledek se odešle. Smaže se až těsně před navigací.
    saveProgress(state, lecture, userId, Date.now())
  }, [state, phase.kind, lecture, userId])

  /* --- časomíra běží jen během otázky ----------------------------------- */
  const now = useNow(phase.kind === 'question')

  /* --- dokončení -------------------------------------------------------- */
  const finished = phase.kind === 'finished'

  // Po dokončení nejdřív ukázat mezikrok, teprve pak verdikt.
  useEffect(() => {
    if (phase.kind !== 'finished') {
      setResultsRevealed(false)
      return undefined
    }
    const timer = window.setTimeout(() => setResultsRevealed(true), RESULTS_PENDING_MS)
    return () => window.clearTimeout(timer)
  }, [phase.kind])

  /** Přechod dál — z tlačítka i z časovače, ale proběhne jen jednou. */
  const finishNow = useCallback(() => {
    if (phase.kind !== 'finished' || navigated.current) return
    navigated.current = true
    const url =
      successUrl ?? buildFinishUrl({ userId, moduleId: lecture, result: phase.result })
    // Až teď: do této chvíle byl výsledek jen v uloženém postupu.
    clearProgress(lecture, userId)
    onFinish(url)
  }, [phase, successUrl, userId, lecture, onFinish])

  // Časovač volá vždy aktuální verzi. Kdyby byl `finishNow` v závislostech,
  // každá nová identita `onFinish` od rodiče by časovač restartovala.
  const finishNowRef = useRef(finishNow)
  useEffect(() => {
    finishNowRef.current = finishNow
  }, [finishNow])

  // Po zobrazení verdiktu se přejde samo (viz AUTO_FINISH_MS).
  useEffect(() => {
    if (!autoFinish || !finished || !resultsRevealed) return undefined
    const timer = window.setTimeout(() => finishNowRef.current(), AUTO_FINISH_MS)
    return () => window.clearTimeout(timer)
  }, [autoFinish, finished, resultsRevealed])

  /* --- akce ------------------------------------------------------------- */
  const answer = useCallback(
    (selected: number) => dispatch({ type: 'answer', selected, now: Date.now() }),
    [],
  )
  const proceed = useCallback(() => dispatch({ type: 'continue', now: Date.now() }), [])

  const videoEnded = useCallback(() => dispatch({ type: 'videoEnded', now: Date.now() }), [])
  const videoFailed = useCallback(
    (message: string) => dispatch({ type: 'videoFailed', message }),
    [],
  )
  /*
   * Kolikrát už uživatel v tomhle kurzu musel video přeskočit.
   *
   * Když firemní síť blokuje YouTube, nerozjede se ŽÁDNÉ video — a bez
   * tohohle počítadla by uživatel u každého dalšího kroku znovu čekal
   * plných 15 vteřin, než se nabídne přeskočení. Po druhém přeskočení
   * se čekání zkrátí, protože je zřejmé, že videa tu prostě nepojedou.
   */
  const [videoSkips, setVideoSkips] = useState(0)
  const skipVideo = useCallback(() => {
    setVideoSkips((n) => n + 1)
    dispatch({ type: 'skipVideo', now: Date.now() })
  }, [])
  const pauseCourse = useCallback(() => dispatch({ type: 'pause', now: Date.now() }), [])
  const resumeCourse = useCallback(() => dispatch({ type: 'resume', now: Date.now() }), [])

  const paused = isPaused(state)

  /*
   * Po zodpovězení se pokračuje SAMO.
   *
   * Původní aplikace vyžadovala kliknutí na „Klikněte kdekoliv pro
   * pokračování". Nebyl to záměr — `onLoad` na `<div>` se nikdy nespustí,
   * takže vývojář potřeboval, aby uživatel kliknutím vynutil překreslení.
   *
   * Prodleva nechá vidět, jestli byla odpověď správně. Kliknutí ji přeskočí.
   * Během pauzy se nepokračuje.
   */
  const awaitingContinue = phase.kind === 'feedback' && !paused
  useEffect(() => {
    if (!awaitingContinue) return undefined
    const timer = window.setTimeout(proceed, FEEDBACK_MS)
    return () => window.clearTimeout(timer)
  }, [awaitingContinue, proceed])

  const onVideo = phase.kind === 'introVideo' || phase.kind === 'outroVideo'

  const togglePause = useCallback(() => {
    if (paused) resumeCourse()
    else pauseCourse()
  }, [paused, pauseCourse, resumeCourse])

  /** Nabídka se ukazuje jen v průběhu kurzu, ne na chybových a koncových obrazovkách. */
  const menu =
    onVideo || phase.kind === 'question' || phase.kind === 'feedback' ? (
      <PauseMenu
        paused={paused}
        onPause={pauseCourse}
        onResume={resumeCourse}
        onQuit={onQuit}
        language={language}
        // U videa má pauzu spodní pruh přehrávače — v railu by ležela v obraze
        showPauseToggle={!onVideo}
      />
    ) : null

  /* --- vykreslení ------------------------------------------------------- */
  switch (phase.kind) {
    case 'loading':
      return <LoadingScreen />

    case 'error':
      return phase.error.kind === 'courseLoad' ? (
        <ErrorScreen
          title="Kurz se nepodařilo načíst"
          detail={`${phase.error.message}. Zkontrolujte připojení k internetu.`}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      ) : (
        <ErrorScreen
          title="Video se nepodařilo přehrát"
          detail={phase.error.message}
          onSkip={skipVideo}
          skipLabel="Pokračovat bez videa"
        />
      )

    case 'introVideo':
    case 'outroVideo': {
      const url = currentVideoUrl(state)
      if (url === null) {
        // Reducer tenhle stav nedovolí, ale UI nesmí spadnout ani tak.
        return (
          <ErrorScreen
            title="Video chybí"
            detail="U tohoto kroku není vyplněná adresa videa."
            onSkip={skipVideo}
          />
        )
      }
      return (
        <>
          <VideoPlayer
            key={url}
            url={url}
            controls={state.course?.controls ?? true}
            // Po opakovaném přeskočení nenechat uživatele čekat celých 15 s.
            {...(videoSkips >= 2 ? { stallAfterMs: 5_000 } : {})}
            onEnded={videoEnded}
            onError={videoFailed}
            onSkip={skipVideo}
            paused={paused}
            onTogglePause={togglePause}
          />
          {menu}
        </>
      )
    }

    case 'question':
    case 'feedback': {
      if (!step) return <LoadingScreen />
      return (
        <>
          <QuestionScreen
            step={step}
            answered={
              phase.kind === 'feedback' && state.lastAnswer
                ? { selected: state.lastAnswer.selected, correct: phase.correct }
                : null
            }
            timer={phase.kind === 'question' ? timerLevel(state, now) : 'none'}
            onAnswer={answer}
            language={language}
          />
          {menu}
        </>
      )
    }

    case 'finished': {
      // Nejdřív mezikrok „Modul dokončen — vyhodnocuji…“, pak verdikt.
      if (!resultsRevealed) return <ResultsPendingScreen language={language} />

      const { result } = phase
      const percent =
        result.totalMaxPoints > 0 ? (result.totalPoints / result.totalMaxPoints) * 100 : 0
      return (
        <FinishedScreen
          passed={result.passed}
          percent={percent}
          language={language}
          destination={!autoFinish ? 'manual' : successUrl !== undefined ? 'demo' : 'profile'}
          onContinue={finishNow}
        />
      )
    }
  }

  // Nedosažitelné — TypeScript hlídá úplnost switche.
  return <LoadingScreen />
}
